<script setup>
import { computed } from 'vue';
import { copyToClipboard, l } from '@utils/toolbox';
import { events } from '@js/utils/event';
import { toaster } from '@js/components/toast';
import { trackEvent } from '@utils/googleDataLayer';

const props = defineProps({
	item: {
		type: Object,
		required: true
	}
});

const emit = defineEmits(['observed']);

const isArlandaExpress = computed(() => props.item?.id === 'arlandaexpress');
const isLindskrog = computed(() => props.item?.id === 'lindskrog');

const version = js_params.version;

function trackClick (item) {
	const analyticsEventMap = {
		bookingcom: events.travelEssentialsClickBooking,
		rentalcar: events.travelEssentialsClickRentalCar,
		airporttransfer: events.travelEssentialsClickAirportTransfer,
		forex: events.travelEssentialsClickForex,
		arlandaexpress: events.travelEssentialsClickArlandaExpress,
		airalo: events.travelEssentialsClickAiralo,
		northmill: events.travelEssentialsClickNorthmill,
		lindskrog: events.travelEssentialsClickLindskrog || true
	};
	if (!analyticsEventMap[item.id]) {
		console.error(`No analytics event mapped for travel essential item id: ${item.id}`);
		return;
	}

	if (trackEvent) {
		trackEvent.fakeEcommerceTravelEssential(item.id);
	}
}

function copyCampaignCode (item) {
	copyToClipboard(item).then(() => {
		toaster.newToast(l('kallebakom.forex.clipboard'), 'info', 'top');
	});
}

// Track when the element is observed (for viewability tracking), at least 80% of the element should be visible
const travelEssentialCard = ref(null);

onMounted(() => {
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting && entry.intersectionRatio >= 0.8) {
				// Emit the observed event to parent instead of logging directly
				emit('observed', props.item.id);
				observer.unobserve(entry.target);
			}
		});
	}, {
		threshold: [0, 0.8]
	});

	if (travelEssentialCard.value) {
		observer.observe(travelEssentialCard.value);
	}
});
</script>

<template>
  <a
    ref="travelEssentialCard"
    :href="item?.forwardURL"
    target="_blank"
    class="kallebakom__item"
    :class="{ active: item?.selected }"
    :data-kalleview="item?.id"
    @click="trackClick(item)"
  >
    <div class="kallebakom__header">
      <img
        v-if="item?.options?.svgOnTab && item?.id !== 'rentalcar' && item?.id !== 'vaccindirekt'"
        class="kallebakom__multimobilemobile-logo"
        :src="`/static/v${version}/images/global/thirdparty_${item.id}.svg`"
        alt=""
      >
      <img
        v-if="item?.options?.svgOnTab && item?.id === 'rentalcar'"
        class="kallebakom__multimobilemobile-logo"
        :src="`/static/v${version}/images/global/thirdparty_rentalcars.svg`"
        alt=""
      >
      <img
        v-if="item?.options?.svgOnTab && item?.id === 'vaccindirekt'"
        class="kallebakom__multimobilemobile-logo"
        :src="`/static/v${version}/images/global/thirdparty_vaccindirekt-square.png`"
        alt=""
      >
      <h2 v-if="item?.title" class="kallebakom__title">
        {{ item.title }}
      </h2>
    </div>

    <div class="kallebakom__multimobile-content">
      <p
        v-if="item?.options?.hugeTitle"
        class="kallebakom__huge-title"
        v-html="item?.id === 'airalo' ? l('kallebakom.airalo.gspHugeTitle') : item.options.hugeTitle"
      />

      <img
        v-if="isArlandaExpress"
        class="kallebakom__arlanda--arrow-first"
        :src="`/static/v${version}/images/kallebakom/arrow_arlanda.svg`"
        alt=""
      >

      <!-- Link block with image and display info -->
      <div
        target="_blank"
        class="kallebakom__item-content link-item"
        role="button"
        :data-kalleid="item.id"
      >
        <div v-if="item?.mainImg" class="kallebakom__item-content--image">
          <img
            :src="item.mainImg"
            loading="lazy"
            alt=""
          >
        </div>

        <div v-if="item?.display" class="kallebakom__display">
          <template v-if="Array.isArray(item.display?.sellpoint)">
            <span v-for="(s, idx) in item.display.sellpoint" :key="idx">
              <i
                v-if="s.icon"
                class="icon"
                :class="`icon-${s.icon}`"
              />
              {{ s.text }}
            </span>
          </template>
        </div>
      </div>

      <!-- ArlandaExpress decorations -->
      <template v-if="isArlandaExpress">
        <img
          class="kallebakom__arlanda--arrow-last"
          :src="`/static/v${version}/images/kallebakom/arrow_arlanda.svg`"
          alt=""
        >
        <div class="kallebakom__arlanda--description">
          <img
            class="kallebakom__arlanda--arrow-blacks"
            :src="`/static/v${version}/images/kallebakom/arrow_arlanda--black.svg`"
            alt=""
          >
          <span v-html="l('kallebakom.arlandaexpress.sellPointBlackArrows')" />
        </div>
        <img
          class="kallebakom__arlanda--logo"
          :src="`/static/v${version}/images/kallebakom/arlandaexpress.svg`"
          alt=""
        >
      </template>

      <!-- Copy code input -->
      <div
        v-if="item?.extraInfo?.copyCodeInput"
        class="kallebakom-modal--inputbox discountBox"
      >
        <label for="flygersorForexDiscount" class="share-modal--inputlabel">
          {{ l('kallebakom.auxiliar.campaigncode') }}
        </label>
        <input
          id="flygersorForexDiscount"
          disabled
          type="text"
          name="flygersorForexDiscount"
          class="share-modal--input"
          :value="item.extraInfo.copyCodeInput"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          tabindex="0"
        >
        <button
          class="abBtn--blueWhite abBtn abBtn--flex copyBtn"
          @click.stop.prevent="copyCampaignCode(item?.extraInfo?.copyCodeInput)"
        >
          {{ l('travelEssentials.forex.copyText') }}
        </button>
      </div>

      <!-- Huge title (former extra items) -->
      <div class="kallebakom__alternatives">
        <div class="kallebakom__other">
          <p v-if="item?.options?.travelEssentialsSimple">
            {{ l(`travelEssentials.${item.id}.hugeTitle`) }}
          </p>
        </div>
      </div>

      <!-- CTA -->
      <div
        class="link-item abBtn abBtn--blueWhite abBtn--flex kallebakom_cta"
        :data-kalleid="item.id"
      >
        {{ l(`kallebakom.${item.id}.cta`) }}
      </div>
    </div>
  </a>
</template>

<style src="@/src/sass/showresult/_kallebakom.scss"></style>

<style lang="scss" scoped>
  .kallebakom__header {
    display: flex;
    min-height: 6rem;
    justify-content: center;
    align-content: center;

    .kallebakom__multimobilemobile-logo {
      display: inline-block;
      position: static;
      flex: 20% 1 1;
      max-width: 10rem;
      padding: .5rem 1.5rem;
      object-fit: contain;
      border-radius: 0;
      margin: 0;
      transform: none;
      height: auto;
    }

    .kallebakom__title {
      font-size: 1.6rem;
      display: flex;
      align-items: center;
      padding: 0 1rem;
      flex: 80% 1 1;
      justify-content: center;
    }
  }

  .kallebakom__item {
    border-radius: .9rem;
    text-decoration: none;
  }

  .link-item.abBtn.abBtn--blueWhite.abBtn--flex.kallebakom_cta {
    display: flex;
  }

  [data-kalleview=forex] {
    .copyBtn {
      padding: 0.95rem 1.6rem;
      font-weight: normal;
      background-color: $color-blue-900;
      font-size: 1.6rem;
    }

    .kallebakom__display {
      padding: 0 .5rem;
      font-size: 1.4rem;
      text-align: left;

      span:last-of-type {
        @include breakpoint_down(lg) {
          position: initial;
        }
      }
    }

    .kallebakom__multimobile {
      padding-top: 1rem;
    }
  }

  .kallebakom__item .kallebakom_cta {
    margin-top: 1rem;
  }

  [data-kalleview=rentalcar] {
    .kallebakom__item-content {
      height: 14rem;
    }

    .kallebakom__item-content--image {
      max-width: calc(100% + 2rem);
      width: 100%;
      margin: -1rem;
      border-radius: 0;
      height: calc(100% - 12.5rem);
      position: absolute;
      top: 7rem;

      img {
        height: 100%;
        object-fit: cover;
      }

      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(45deg, $color-blue-800, transparent);
      }
    }

    .kallebakom__alternatives .kallebakom__other {
      position: absolute;
      color: $white;
      text-shadow: .3rem .3rem $black;
      font-size: 4rem;
      text-align: left;
      max-width: 55%;
      font-weight: bold;
      line-height: 4rem;
      letter-spacing: -0.2rem;
      height: 100%;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  [data-kalleview=arlandaexpress] {
    .kallebakom_cta {
      background-color: $color-blue-900;
    }

    .kallebakom__huge-title {
      font-size: 3rem;
      line-height: 3rem;
    }

    .kallebakom__multimobile-content {
      min-height: auto;
    }

    .kallebakom__multimobile-content .kallebakom__arlanda--arrow-first {
      height: 100%;
      right: 10%;
      max-height: 50%;
    }

    .kallebakom__multimobile-content .kallebakom__arlanda--arrow-last {
      height: 100%;
      right: 0;
      max-height: 50%;
    }

    .kallebakom__multimobile-content .kallebakom__arlanda--description {
      @include breakpoint_down(md) {
        display: none;
      }
    }

    .kallebakom_cta {
      @include breakpoint_down(md) {
        position: static;
        margin-bottom: -1rem;
      }
    }

    .kallebakom__arlanda--logo {
      @include breakpoint_down(md) {
        display: none;
      }
    }
  }

  .kallebakom__item__wrapper:last-of-type [data-kalleview=arlandaexpress] .kallebakom__huge-title {
    margin-top: 0;
    margin-bottom: 6rem;
  }

  [data-kalleview=airalo] {
    .kallebakom__huge-title {
      margin: 0;
      font-size: 3rem;
      line-height: 3rem;
      font-weight: normal;
      font-style: italic;
      color: $black;

      @include breakpoint_down(md) {
        margin: 1rem 0;
      }
    }

    .kallebakom__alternatives {
      display: none;
    }

    .kallebakom__other p {
      max-width: 50%;
      margin: 0;
      font-size: 1.3rem;

      @include breakpoint_down(md) {
        font-size: 1.2rem;
      }
    }

    .kallebakom__item-content {
      bottom: 6.5rem;
      right: -5rem;
    }

    .kallebakom__item-content--image {
      min-width: 12rem;

      @include breakpoint_down(md) {
        min-width: 10rem;
        max-width: fit-content;
      }

      img {
        rotate: -15deg;
        transform: scale(1.15);

        @include breakpoint_down(md) {
          transform: scale(1);
          width: 14rem;
          rotate: 0deg;
        }
      }
    }
  }

  [data-kalleview=forex] {
    .kallebakom-modal--inputbox {
      margin-top: 1rem;
    }

    .kallebakom__multimobile-content {
      padding-top: 1rem;
    }

    .kallebakom__display :last-of-type {
      position: relative;
      bottom: 0;
    }
  }

  [data-kalleview=northmill] {
    @include breakpoint_up(md) {
      .kallebakom__multimobilemobile-logo {
        background: #8c5ac8;
      }

      .kallebakom__huge-title {
        max-width: 100%;
        font-size: 3rem;
        line-height: 3rem;
        padding: 0;
        position: relative;
        letter-spacing: -.1rem;
        text-shadow: .1rem .3rem .6rem $black;
        pointer-events: none;
      }

      .kallebakom__display {
        white-space: nowrap;
        position: relative;
        padding: 0;
        font-size: 1.4rem;
        font-weight: normal;
        gap: 0.1rem;
      }

      .kallebakom__item-content--image {
        max-width: 10rem;
        border-radius: 0;
      }

      .kallebakom_cta {
        position: relative;
        z-index: 2;
      }

      .kallebakom__multimobile-content {
        img {
          height: auto;
          max-width: 10rem;
          float: right;
        }

        .kallebakom__item-content {
          justify-content: space-between;
          padding: 0;
          margin-top: -20%;
          padding: 1rem 0;
        }

        &::after {
          content: '';
          background-color: $white;
          width: 100%;
          height: 6.5rem;
          position: absolute;
          bottom: 0;
          left: 0;
          z-index: 0;
        }
      }
    }

    // not mobile but grid of two
    @include breakpoint_between(md, lg) {
      .kallebakom__multimobile-content .kallebakom__item-content {
        align-items: center;
        margin-top: 0;

        img {
          float: none;
        }
      }
    }
  }

  [data-kalleview=bookingcom] {
    .kallebakom__other,
    .kallebakom__item-content {
      display: flex;
    }

    .kallebakom__alternatives {
      display: none;
    }
    .kallebakom__item-content {
      height: 14rem;
    }

    .kallebakom__item-content--image {
      max-width: calc(100% + 2rem);
      width: 100%;
      margin: -1rem;
      border-radius: 0;
      height: calc(100% - 12.5rem);
      position: absolute;
      top: 7rem;

      img {
        height: 100%;
        object-fit: cover;
      }

      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(45deg, $color-blue-800, transparent);
      }
    }

    .kallebakom__alternatives .kallebakom__other {
      position: absolute;
      color: $white;
      text-shadow: .3rem .3rem $black;
      font-size: 3.6rem;
      text-align: left;
      max-width: 55%;
      font-weight: bold;
      line-height: 3.6rem;
      letter-spacing: -0.2rem;
      height: 100%;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      p {
        margin-top: 3rem;
      }
    }
  }

  $lindskrog--blue: #8ab1f8;
  $lindskrog--black: #282729;
  $lindskrog--yellow: #fff06c;

  [data-kalleview=lindskrog] {
    overflow: hidden;
    border: 1px solid rgba(23, 39, 69, .12);
    background: $white;
    color: #22242a;
    box-shadow: 0 .8rem 2.4rem rgba(31, 55, 95, .12);

    .kallebakom_cta {
      margin: 1rem 0;
    }

    .kallebakom__alternatives {
      display: none;
    }

    .kallebakom__huge-title {
      max-width: 100%;
      margin: 0;
      font-size: 3.2rem;
      line-height: 1.2;
      padding: 0;
      flex: 1 1 0;
      height: auto;
      padding: 1rem .5rem;
    }

    .kallebakom__item-content {
      padding: 0;

      @include breakpoint_down(md) {
        margin-bottom: 1rem;
      }
    }

    .kallebakom__display {
      font-size: 1.4rem;
      margin: auto auto auto 0;

      span::before {
        margin-right: 0;
      }
    }

    .kallebakom__multimobile-content {
      flex-direction: column;
      justify-content: flex-end;
      padding-top: 0;
    }

    .kallebakom__header .kallebakom__multimobilemobile-logo {
      max-width: 15rem;
      mix-blend-mode: multiply;
    }
  }

  [data-kalleview=flygbussarna] {
    .kallebakom_cta {
      background-color: $color-blue-900;
      margin: auto 1rem 1rem;
      z-index: 1;

      @include breakpoint_down(md) {
        margin: 1rem;
      }
    }

    .kallebakom__title {
      @include breakpoint_down(md) {
        color: #333;
      }
    }

    .kallebakom__alternatives {
      z-index: 2;
      width: 100%;

      @include breakpoint_down(md) {
        position: absolute;
      }
    }

    .kallebakom__multimobile-content {
      min-height: auto;
      background-color: #f2f2f3;
    }

    .kallebakom__header {
      background: linear-gradient(90deg, #ef3e33 0 20%, #f5a623 20% 40%, #f2e51c 40% 60%, #009a78 60% 80%, #3888c8 80% 100%) bottom / 100% 0.6rem no-repeat, #fff;

      h2 {
        background: none;
      }
    }

    .kallebakom__header .kallebakom__multimobilemobile-logo {
      max-width: 15rem;
      mix-blend-mode: multiply;
    }

    .kallebakom__other {
      font-weight: bold;
      font-size: 2.2rem;
      max-width: 95%;
      margin: auto;

      @include breakpoint_down(md) {
        max-width: 90%;
        font-size: clamp(2.4rem, 6vw, 3rem);
      }
    }

    .kallebakom__item-content--image img {
      mix-blend-mode: normal;
    }
  }
</style>
